import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, Check, X, Tag, Truck, ShieldCheck, RotateCcw } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { storeApi, formatPrice, productUrl, formatStoreError } from "@/lib/storefront";
import SEO from "@/components/storefront/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/storefront/JsonLd";

const PROMO_KEY = "store_promo_v1";

function readPromo() {
  try {
    const raw = localStorage.getItem(PROMO_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.warn("Promo read failed", err?.message || err);
    return null;
  }
}

function writePromo(p) {
  try {
    if (p) localStorage.setItem(PROMO_KEY, JSON.stringify(p));
    else localStorage.removeItem(PROMO_KEY);
  } catch (err) {
    console.warn("Promo write failed", err?.message || err);
  }
}

export default function Cart() {
  const { items, subtotal, count, updateQty, removeItem, clear } = useCart();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [promo, setPromoState] = useState(() => readPromo());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const currency = items[0]?.currency || "USD";

  const setPromo = (p) => {
    setPromoState(p);
    writePromo(p);
  };

  const baseShipping = subtotal >= 75 || items.length === 0 ? 0 : 8;
  const freeShip = !!promo?.free_shipping;
  const shippingEst = freeShip ? 0 : baseShipping;
  // Recompute promo discount in case subtotal changed since it was applied
  let appliedDiscount = 0;
  if (promo && !freeShip) {
    if (promo.type === "percent") appliedDiscount = +(subtotal * (promo.value / 100)).toFixed(2);
    else if (promo.type === "fixed") appliedDiscount = +Math.min(promo.value, subtotal).toFixed(2);
  }
  const total = Math.max(0, subtotal - appliedDiscount + shippingEst);

  const applyPromo = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await storeApi.post("/promo/validate", { code: code.trim(), subtotal });
      setPromo(data);
      setCode("");
    } catch (err) {
      setError(formatStoreError(err));
    } finally {
      setBusy(false);
    }
  };

  const removePromo = () => {
    setPromo(null);
    setError("");
  };

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-10 sm:px-6 sm:py-14 md:px-10 md:py-20" data-testid="cart-page">
      <SEO title="Your bag" noindex />
      <JsonLd data={breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Bag", url: "/cart" },
      ])} />
      <div className="mb-8 text-[10px] uppercase tracking-[0.2em] text-[#6B5158] sm:mb-10 sm:text-xs sm:tracking-[0.25em]">
        <Link to="/" className="hover:text-[#2B2127]">Home</Link>
        <span className="mx-2">/</span>
        <span>Bag</span>
      </div>

      <div className="mb-8 flex flex-wrap items-end justify-between gap-3 sm:mb-10">
        <h1 className="font-display text-4xl sm:text-5xl md:text-[72px]">Your <span className="font-display-soft">bag</span></h1>
        {count > 0 && <div className="text-sm text-[#6B5158] store-nums">{count} {count === 1 ? "item" : "items"}</div>}
      </div>

      {items.length === 0 ? (
        <div className="py-20 text-center">
          <ShoppingBag size={56} strokeWidth={1.2} className="mx-auto mb-6 text-[#6B5158]/30" />
          <h2 className="font-display text-3xl">Your bag is <span className="font-display-soft">empty</span></h2>
          <p className="mt-2 text-sm text-[#6B5158]">Quiet objects for everyday rituals — let's find something you'll love.</p>
          <Link to="/catalog" className="mt-8 inline-flex rounded-full bg-[#2B2127] px-8 py-3.5 text-sm text-white transition-colors hover:bg-[#9D5C6C]" data-testid="empty-cart-shop">
            Shop products
          </Link>
          <div className="mt-12 flex items-center justify-center gap-8 text-[10px] uppercase tracking-[0.2em] text-[#6B5158]">
            <span className="inline-flex items-center gap-1.5"><Truck size={12} /> Fast delivery</span>
            <span className="inline-flex items-center gap-1.5"><RotateCcw size={12} /> 30-day returns</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} /> Secure checkout</span>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[1fr_380px]">
          {/* Items */}
          <div className="divide-y divide-[#E6D4CC] border-y border-[#E6D4CC]">
            {items.map((it) => (
              <div key={it.product_id} className="flex gap-3 py-5 sm:gap-5 sm:py-6" data-testid={`cart-row-${it.product_id}`}>
                <Link to={productUrl(it)} className="block h-28 w-20 flex-shrink-0 overflow-hidden rounded bg-[#F1E3DC] sm:h-32 sm:w-24 md:h-40 md:w-32">
                  {it.image && <img src={it.image} alt="" className="h-full w-full object-cover" />}
                </Link>
                <div className="flex min-w-0 flex-1 flex-col">
                  <Link to={productUrl(it)} className="line-clamp-2 break-words font-display text-base leading-tight hover:text-[#9D5C6C] sm:text-lg">{it.title}</Link>
                  {it.sku && <div className="mt-1 font-mono text-[10px] text-[#6B5158] sm:text-xs">{it.sku}</div>}
                  <div className="mt-3 flex flex-wrap items-end justify-between gap-2 sm:mt-auto">
                    <div className="flex items-center gap-1 rounded-full border border-[#E6D4CC]">
                      <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="px-3 py-2" aria-label="Decrease"><Minus size={14} /></button>
                      <span className="min-w-[28px] text-center text-sm store-nums">{it.quantity}</span>
                      <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="px-3 py-2" aria-label="Increase"><Plus size={14} /></button>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="store-nums text-sm font-medium">{formatPrice(it.price * it.quantity, it.currency)}</div>
                      <button onClick={() => removeItem(it.product_id)} className="text-[#6B5158] hover:text-[#2B2127]" aria-label="Remove">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="pt-4 text-xs text-[#6B5158]">
              <button onClick={clear} className="underline decoration-[#9D5C6C] underline-offset-4 hover:text-[#2B2127]" data-testid="cart-clear-btn">Clear bag</button>
            </div>
          </div>

          {/* Summary */}
          <aside className="h-fit rounded-md border border-[#E6D4CC] bg-white p-6">
            <h3 className="font-display text-2xl">Order summary</h3>

            {/* Promo code */}
            {promo ? (
              <div
                className="mt-5 flex items-center justify-between gap-2 rounded-md bg-[#F1E3DC] px-3 py-2.5"
                data-testid="promo-applied"
              >
                <div className="flex items-center gap-2 text-sm">
                  <Tag size={14} className="text-[#9D5C6C]" />
                  <div>
                    <div className="font-mono text-xs uppercase tracking-wider text-[#2B2127]">{promo.code}</div>
                    <div className="text-xs text-[#6B5158]">{promo.label}</div>
                  </div>
                </div>
                <button onClick={removePromo} aria-label="Remove promo" className="rounded-full p-1.5 text-[#6B5158] hover:bg-white hover:text-[#2B2127]" data-testid="promo-remove-btn">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <form onSubmit={applyPromo} className="mt-5">
                <label className="mb-1.5 block text-[10px] uppercase tracking-[0.2em] text-[#6B5158]">Promo code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => { setCode(e.target.value); if (error) setError(""); }}
                    placeholder="WELCOME10"
                    className="flex-1 rounded-full border border-[#E6D4CC] bg-white px-4 py-2.5 text-sm uppercase tracking-wider outline-none focus:border-[#2B2127]"
                    data-testid="promo-input"
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    disabled={busy || !code.trim()}
                    className="flex-shrink-0 rounded-full border border-[#2B2127] px-4 py-2.5 text-sm text-[#2B2127] transition-colors hover:bg-[#2B2127] hover:text-white disabled:opacity-40"
                    data-testid="promo-apply-btn"
                  >
                    {busy ? "..." : "Apply"}
                  </button>
                </div>
                {error && <div className="mt-2 text-xs text-rose-600" data-testid="promo-error">{error}</div>}
                <div className="mt-2 text-[11px] text-[#6B5158]">Try <span className="font-mono uppercase">WELCOME10</span> for 10% off.</div>
              </form>
            )}

            <dl className="mt-5 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-[#6B5158]">Subtotal</dt><dd className="store-nums">{formatPrice(subtotal, currency)}</dd></div>
              {appliedDiscount > 0 && (
                <div className="flex justify-between text-[#9D5C6C]" data-testid="promo-discount-row">
                  <dt className="inline-flex items-center gap-1"><Check size={12} /> Promo {promo.code}</dt>
                  <dd className="store-nums">−{formatPrice(appliedDiscount, currency)}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-[#6B5158]">Shipping</dt>
                <dd className="store-nums">{shippingEst === 0 ? (freeShip ? <span className="text-[#9D5C6C]">Free (promo)</span> : "Free") : formatPrice(shippingEst, currency)}</dd>
              </div>
              {!freeShip && subtotal < 75 && subtotal > 0 && <div className="flex justify-between text-xs text-[#9D5C6C]"><dt>Free delivery from</dt><dd>$75</dd></div>}
            </dl>
            <div className="mt-5 flex items-baseline justify-between border-t border-[#E6D4CC] pt-4">
              <span className="font-display text-lg">Total</span>
              <span className="font-display store-nums text-2xl" data-testid="cart-total">{formatPrice(total, currency)}</span>
            </div>
            <button onClick={() => nav("/checkout")} className="mt-6 w-full rounded-full bg-[#2B2127] px-6 py-4 text-sm text-white transition-colors hover:bg-[#9D5C6C]" data-testid="proceed-checkout-btn">
              Continue to checkout <ArrowRight size={14} className="ml-1 inline-block" />
            </button>
            <Link to="/catalog" className="mt-3 block text-center text-sm text-[#6B5158] hover:text-[#2B2127]">or continue shopping</Link>

            {/* Trust microline */}
            <div className="mt-6 flex items-center justify-between border-t border-[#E6D4CC] pt-4 text-[10px] uppercase tracking-[0.18em] text-[#6B5158]">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} /> Secure</span>
              <span className="inline-flex items-center gap-1.5"><Truck size={12} /> Fast ship</span>
              <span className="inline-flex items-center gap-1.5"><RotateCcw size={12} /> Easy returns</span>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
